import React, { useEffect, useState } from "react";
import { toast } from "react-toastify";
import { FaTimes, FaTags, FaLink } from "react-icons/fa";

/**
 * KeywordsStep
 * ------------------------------------------------------------------
 * Step 3 of the brand DM automation wizard.
 *
 * The brand specifies one or more keywords. When a comment on the
 * tracked post contains ANY of these (case-insensitive substring
 * match, performed on the backend), the automation fires.
 *
 * The optional Product Link is just stored alongside the automation
 * and made available as a `{{product_link}}` placeholder in the DM
 * message in the next step.
 */
const KeywordsStep = ({ data, onNext, onBack }) => {
  const [keyword, setKeyword] = useState("");
  const [keywordsList, setKeywordsList] = useState(data?.keywords || []);
  const [productLink, setProductLink] = useState(data?.productLink || "");

  useEffect(() => {
    if (data?.keywords) setKeywordsList(data.keywords);
    if (data?.productLink !== undefined) {
      setProductLink(data.productLink || "");
    }
  }, [data?.keywords, data?.productLink]);

  const handleAdd = (event) => {
    event.preventDefault();
    const cleaned = keyword.trim().toLowerCase();
    if (!cleaned) return;
    if (keywordsList.includes(cleaned)) {
      toast.info("Keyword already added");
      return;
    }
    if (cleaned.length > 60) {
      toast.error("Keyword is too long (max 60 chars)");
      return;
    }
    setKeywordsList((prev) => [...prev, cleaned]);
    setKeyword("");
  };

  const handleRemove = (kw) => {
    setKeywordsList((prev) => prev.filter((k) => k !== kw));
  };

  const handleSubmit = () => {
    if (keywordsList.length === 0) {
      toast.error("Please add at least one keyword");
      return;
    }
    if (productLink && !/^https?:\/\//i.test(productLink.trim())) {
      toast.error(
        "Product link must start with http:// or https:// (or leave it blank)"
      );
      return;
    }

    onNext({
      keywords: keywordsList,
      productLink: productLink.trim(),
    });
  };

  return (
    <div className="space-y-5">
      <header>
        <h2 className="text-base md:text-xl font-semibold text-gray-900">
          Keywords
        </h2>
        <p className="mt-1 text-[11px] md:text-sm text-gray-600">
          The DM will fire when a comment on this post contains any of these
          keywords (case-insensitive).
        </p>
      </header>

      <form onSubmit={handleAdd} className="flex items-center gap-2">
        <div className="flex flex-1 items-center rounded-lg border border-gray-300 bg-white focus-within:border-orange-500 focus-within:ring-2 focus-within:ring-orange-100">
          <span className="pl-3 text-gray-400">
            <FaTags />
          </span>
          <input
            type="text"
            placeholder="Add a keyword and press Enter"
            value={keyword}
            onChange={(event) => setKeyword(event.target.value)}
            className="w-full bg-transparent p-2 text-sm focus:outline-none"
          />
        </div>
        <button
          type="submit"
          className="rounded-lg bg-orange-600 px-4 py-2 text-sm font-semibold text-white transition hover:bg-orange-700"
        >
          Add
        </button>
      </form>

      <div className="flex min-h-[40px] flex-wrap gap-2">
        {keywordsList.length === 0 ? (
          <p className="text-[11px] text-gray-400">No keywords added yet.</p>
        ) : (
          keywordsList.map((kw) => (
            <span
              key={kw}
              className="inline-flex items-center gap-2 rounded-full bg-orange-100 px-3 py-1 text-xs font-medium text-orange-700"
            >
              {kw}
              <button
                type="button"
                onClick={() => handleRemove(kw)}
                aria-label={`Remove ${kw}`}
                className="text-orange-500 hover:text-red-500"
              >
                <FaTimes className="h-3 w-3" />
              </button>
            </span>
          ))
        )}
      </div>

      <div>
        <label className="block text-xs md:text-sm font-medium text-gray-700">
          Product Link{" "}
          <span className="text-[10px] text-gray-400">(optional)</span>
        </label>
        <div className="mt-1 flex items-center rounded-lg border border-gray-300 bg-white focus-within:border-orange-500 focus-within:ring-2 focus-within:ring-orange-100">
          <span className="pl-3 text-gray-400">
            <FaLink />
          </span>
          <input
            type="url"
            placeholder="https://brand.com/product"
            value={productLink}
            onChange={(event) => setProductLink(event.target.value)}
            className="w-full bg-transparent p-2 text-sm focus:outline-none"
          />
        </div>
        <p className="mt-1 text-[10px] text-gray-500">
          Use the <code className="font-mono">{"{{product_link}}"}</code>{" "}
          placeholder in your DM message to drop this in automatically.
        </p>
      </div>

      <div className="flex justify-between pt-2">
        <button
          type="button"
          onClick={onBack}
          className="rounded-lg bg-gray-100 px-4 py-2 text-sm font-medium text-gray-700 transition hover:bg-gray-200"
        >
          Back
        </button>
        <button
          type="button"
          onClick={handleSubmit}
          className="rounded-lg bg-orange-600 px-5 py-2 text-sm font-semibold text-white transition hover:bg-orange-700"
        >
          Next
        </button>
      </div>
    </div>
  );
};

export default KeywordsStep;
